export type PreferredMethod =
  | 'constrained'
  | 'unconstrained'
  | 'carbonCost'
  | 'campaign'

export type Status =
  | 'completed'
  | 'failed'
  | 'unstarted'
  | 'running'
  | 'initiated'
  | 'processing'

export type BaseSourceSiteWorkCentre = {
  id: number
  baseSourceSiteKey: number
  baseSourceSiteCode: string
  workcentreCode: string
  workCentreKey: number
  workcentreCapacity: number
  headSpaceBuffer: number
  preferredTemplate: PreferredMethod
  constrainedStatus: Status
  unconstrainedStatus: Status
  carbonCostStatus: Status
  campaignStatus: Status
  // When this line's template inputs last changed, and on which page; and
  // the completed templates generated before that (API utils/input_changes.py).
  inputsChangedAt?: number | null
  inputsChangedBy?: string | null
  staleMethods?: PreferredMethod[]
}

/** Why a line's templates are out of date, in a sentence. */
export const staleTemplatesText = (
  row: Pick<BaseSourceSiteWorkCentre, 'inputsChangedAt' | 'inputsChangedBy'>,
  methods: string[]
) => {
  const when = row.inputsChangedAt
    ? new Date(row.inputsChangedAt).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'recently'
  const where = row.inputsChangedBy ? ` on ${row.inputsChangedBy}` : ''
  const what =
    methods.length === 1
      ? `The ${methodLabel(methods[0] ?? '')} template was`
      : `${methods.map(methodLabel).join(', ')} templates were`
  return `${what} generated before this line's inputs changed${where} (${when}). Generate again to update ${methods.length === 1 ? 'it' : 'them'}.`
}

export const STATUS_MAP: Record<
  PreferredMethod,
  keyof BaseSourceSiteWorkCentre
> = {
  constrained: 'constrainedStatus',
  unconstrained: 'unconstrainedStatus',
  carbonCost: 'carbonCostStatus',
  campaign: 'campaignStatus',
}

export const createMenuItems = (list: string[]) => {
  return list.map((item) => {
    return { label: item, value: item }
  })
}

/**
 * The methods every template run solves, for each selected work centre (the
 * API's RUN_METHODS). The results page compares them and the user picks one
 * as the work centre's preferredTemplate. carbonCost is left out: the solver
 * treated it exactly as campaign, so it came out identical.
 */
export const RUN_METHODS: PreferredMethod[] = [
  'constrained',
  'unconstrained',
  'campaign',
]

export const METHOD_LABELS: Record<PreferredMethod, string> = {
  constrained: 'Constrained',
  unconstrained: 'Unconstrained',
  carbonCost: 'Carbon cost',
  campaign: 'Campaign',
}

export const methodLabel = (method: string) =>
  METHOD_LABELS[method as PreferredMethod] ?? method

export const SUPPLY_TYPES = ['COUPLED', 'DECOUPLED']
